<template>
  <v-container class="lx-page">
    <header class="lx-page-head">
      <span class="kicker">Research · Backtesting</span>
      <h1>Weekly Back-Testing Report</h1>
      <h4>
        Out-of-sample performance of H2F2 against a standard econometric
        benchmark with no machine learning. Pick a country and a variable: the
        chart replays the realised series alongside both pseudo-out-of-sample
        forecasts over the back-testing window.
        <a href="#" @click.prevent="showMethod = !showMethod">{{
          showMethod ? "Hide methodology" : "Methodology"
        }}</a>
      </h4>
      <p v-if="showMethod" class="lx-method-note">
        H2F2 trains hundreds of specifications and datasets for each variable
        and country and keeps the model, or combination of models, with the best
        pseudo-out-of-sample forecasts over the back-testing period: the 52
        weeks before forecasting starts. The deviation coefficient is the root
        mean squared forecast error (RMSFE) divided by the sample mean; lower is
        better. By construction the H2F2 forecast is at least as good as the
        benchmark.
      </p>
    </header>

    <div class="lx-toolbar">
      <label class="lx-field">
        <span class="lx-field__label">Country</span>
        <select class="form-control minimal" v-model="value">
          <option
            v-for="item in getcontriesConsensusFilter"
            :value="item"
            :key="item.name"
          >
            {{ item.name }}
          </option>
        </select>
      </label>
      <label class="lx-field lx-field--wide">
        <span class="lx-field__label">Variable</span>
        <select class="form-control minimal" v-model="value2">
          <option v-for="item in variable" :value="item" :key="item">
            {{ item }}
          </option>
        </select>
      </label>
      <span class="lx-toolbar__spacer"></span>
      <button class="lx-btn" :disabled="!hasData || playing" @click="play()">
        <v-icon icon="mdi-replay" size="16" class="mr-1"></v-icon>Replay
      </button>
    </div>

    <section class="lx-panel">
      <div class="lx-panel__head">
        <span class="lx-panel__title">{{ chartTitle }}</span>
        <span class="lx-panel__meta" v-if="hasData">
          Weekly · {{ rangeLabel }}
        </span>
      </div>
      <div class="lx-kpi-strip">
        <div class="lx-kpi">
          <span class="lx-kpi__label">Benchmark deviation</span>
          <span class="lx-kpi__value lx-kpi__value--sm">{{ pct(naive) }}</span>
          <span class="lx-kpi__sub">RMSFE / mean, no ML</span>
        </div>
        <div class="lx-kpi">
          <span class="lx-kpi__label">H2F2 deviation</span>
          <span class="lx-kpi__value lx-kpi__value--sm">{{ pct(ml) }}</span>
          <span class="lx-kpi__sub">RMSFE / mean, H2F2</span>
        </div>
        <div class="lx-kpi">
          <span class="lx-kpi__label">Improvement</span>
          <span class="lx-delta" :class="gainClass">{{ gainText }}</span>
          <span class="lx-kpi__sub">H2F2 vs benchmark</span>
        </div>
      </div>
      <div class="lx-chart__frame">
        <div class="lx-legend mb-3" aria-label="Chart legend">
          <span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--hist"></span>Actual</span>
          <span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--blue"></span>H2F2 (ML) out-of-sample</span>
          <span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--bench"></span>Benchmark (no ML) out-of-sample</span>
          <span class="lx-legend__item lx-progress" v-if="playing">{{ progressLabel }}</span>
        </div>
        <div
          ref="container"
          class="lx-chart"
          style="height: 440px"
          role="img"
          :aria-label="chartTitle"
        ></div>
      </div>
    </section>

    <p class="lx-footnote">Dataset updated {{ dateFormat }}.</p>
  </v-container>
</template>

<script>
import anychart from "anychart";
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import {
  createTimeChart,
  setTimeDomain,
  setValueDomain,
  addLine,
  toPoints,
  fmtDate,
  css,
  prefersReducedMotion,
  disposeChart,
} from "@/plugins/lxChart";

const DURATION = 2200; // ms for the full replay

export default {
  name: "backtesting-view",
  data() {
    return {
      value: {},
      value2: "",
      variable: [],
      chart: null,
      sets: [],
      raf: null,
      playing: false,
      shownUntil: null,
      showMethod: false,
    };
  },
  computed: {
    ...mapGetters([
      "getcontriesVizualization",
      "getDateUpdate",
      "getBackteting",
      "getBacktetingDeviation",
      "getBacktetingDeviationMl",
    ]),
    dateFormat() {
      return this.getDateUpdate
        ? moment(this.getDateUpdate).format("D MMM YYYY")
        : "–";
    },
    raw() {
      try {
        return JSON.parse(this.getBackteting || "{}");
      } catch (e) {
        return {};
      }
    },
    series() {
      const v = this.value2;
      return {
        actual: toPoints(this.raw[v + " - Actual"]),
        naive: toPoints(this.raw[v + " - Out-of-Sample - Naive"]),
        ml: toPoints(this.raw[v + " - Out-of-Sample - ML"]),
      };
    },
    hasData() {
      return this.series.actual.length > 0;
    },
    domain() {
      const all = [...this.series.actual, ...this.series.naive, ...this.series.ml];
      if (!all.length) return null;
      const xs = all.map((p) => p[0]);
      const ys = all.map((p) => p[1]);
      return {
        x0: Math.min(...xs),
        x1: Math.max(...xs),
        y0: Math.min(...ys),
        y1: Math.max(...ys),
      };
    },
    rangeLabel() {
      const d = this.domain;
      return d ? `${fmtDate(d.x0, "dd MMM yyyy")} – ${fmtDate(d.x1, "dd MMM yyyy")}` : "";
    },
    progressLabel() {
      return this.shownUntil ? `Replaying · ${fmtDate(this.shownUntil, "dd MMM yyyy")}` : "";
    },
    chartTitle() {
      if (!this.value.name || !this.value2) return "Select a series";
      return `${this.value.name} · ${this.value2}`;
    },
    naive() {
      return this.num(this.getBacktetingDeviation);
    },
    ml() {
      return this.num(this.getBacktetingDeviationMl);
    },
    gain() {
      return this.naive !== null && this.ml !== null ? this.naive - this.ml : null;
    },
    gainText() {
      if (this.gain === null) return "–";
      return `${Math.abs(this.gain).toFixed(2)} pp ${this.gain >= 0 ? "lower" : "higher"} error`;
    },
    gainClass() {
      if (this.gain === null || Math.abs(this.gain) < 0.005) return "lx-delta--flat";
      return this.gain > 0 ? "lx-delta--up" : "lx-delta--down";
    },
    getcontriesConsensusFilter() {
      return this.getcontriesVizualization.filter(
        (i) => i.dataVisualizationFilter === true && i.name !== "International"
      );
    },
  },
  methods: {
    ...mapActions([
      "setCountriesVisualization",
      "setDateUpdate",
      "setDataBacktesting",
      "setDataBacktestingDeviation",
      "setDataBacktestingDeviationMl",
    ]),
    num(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    },
    pct(v) {
      return v === null ? "–" : `${v.toFixed(2)}%`;
    },
    /** "<variable> - Actual" / "<variable> - Out-of-Sample - ..." -> variable list. */
    syncVariables(val) {
      let keys = [];
      try {
        keys = Object.keys(JSON.parse(val || "{}"));
      } catch (e) {
        keys = [];
      }
      this.variable = [...new Set(keys.map((k) => k.slice(0, k.indexOf(" - "))))];
      const keep = this.variable.includes(this.value2) ? this.value2 : this.variable[0] || "";
      if (keep === this.value2) {
        this.setDataBacktestingDeviation({ country: this.value.name, variable: keep });
        this.setDataBacktestingDeviationMl({ country: this.value.name, variable: keep });
        this.$nextTick(() => this.draw());
      } else {
        this.value2 = keep;
      }
    },
    /** Build the chart with empty data sets on a fixed domain, then replay. */
    draw() {
      if (!this.$refs.container) return;
      this.stop();
      if (!this.chart) {
        this.chart = createTimeChart(this.$refs.container);
        this.chart.draw();
      }
      const chart = this.chart;
      chart.removeAllSeries();
      this.sets = [];
      const d = this.domain;
      if (!d) return;
      // the axes stay put while the lines grow, so the eye follows the path
      setTimeDomain(chart, d.x0, d.x1);
      setValueDomain(chart, [d.y0, d.y1]);

      const defs = [
        { key: "naive", name: "Benchmark (no ML)", color: css("--text-mute", "#94a0b4"), dash: "5 4", width: 1.5 },
        { key: "ml", name: "H2F2 (ML)", color: css("--ser-fcst", "#2563b8"), dash: null, width: 2 },
        { key: "actual", name: "Actual", color: css("--ser-hist", "#0d1420"), dash: null, width: 2 },
      ];
      for (const def of defs) {
        const pts = this.series[def.key];
        if (!pts.length) continue;
        const ds = anychart.data.set([]);
        addLine(chart, ds.mapAs({ x: 0, value: 1 }), def);
        this.sets.push({ ds, pts });
      }
      this.play();
    },
    play() {
      this.stop();
      const sets = this.sets;
      if (!sets.length || !this.domain) return;
      const { x0, x1 } = this.domain;
      const show = (until) => {
        for (const s of sets) s.ds.data(s.pts.filter((p) => p[0] <= until));
      };
      if (prefersReducedMotion()) {
        show(x1);
        return;
      }
      this.playing = true;
      show(x0);
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / DURATION);
        const eased = 1 - Math.pow(1 - t, 2);
        const until = x0 + (x1 - x0) * eased;
        this.shownUntil = until;
        show(until);
        if (t < 1) {
          this.raf = requestAnimationFrame(step);
        } else {
          this.raf = null;
          this.playing = false;
          this.shownUntil = null;
        }
      };
      this.raf = requestAnimationFrame(step);
    },
    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      this.playing = false;
      this.shownUntil = null;
    },
  },
  watch: {
    getcontriesVizualization() {
      this.value = this.getcontriesConsensusFilter[0] || {};
    },
    value(val) {
      if (val && val.consensusReport !== undefined) {
        this.setDataBacktesting(val.consensusReport);
      }
    },
    value2(v) {
      if (!v) return;
      this.setDataBacktestingDeviation({ country: this.value.name, variable: v });
      this.setDataBacktestingDeviationMl({ country: this.value.name, variable: v });
      this.$nextTick(() => this.draw());
    },
    getBackteting(val) {
      this.syncVariables(val);
    },
  },
  mounted() {
    this.setDateUpdate();
    // country list / backtest data may already be in the store from another
    // page, in which case their watchers never fire
    if (this.getcontriesConsensusFilter.length) {
      this.value = this.getcontriesConsensusFilter[0];
      if (this.getBackteting) {
        this.$nextTick(() => this.syncVariables(this.getBackteting));
      }
    }
    this.setCountriesVisualization();
  },
  beforeUnmount() {
    this.stop();
    disposeChart(this.chart);
    this.chart = null;
  },
};
</script>
